import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Send, FileText, Image, Film, File, CheckCircle, AlertCircle, Bookmark, Plus, Trash2 } from 'lucide-react';
import { API_BASE, apiFetch } from '../config';
import { useLanguage } from '../i18n';

const SendFile = () => {
  const { t } = useLanguage();
  const [files, setFiles] = useState([]);
  const [recipient, setRecipient] = useState('navoi_mail@nbu.uz');
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem('email_templates') || '[]'); } catch { return []; }
  });
  const [newTemplateName, setNewTemplateName] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('email_templates', JSON.stringify(templates));
  }, [templates]);

  const saveTemplate = () => {
    if (!recipient || !newTemplateName.trim()) return;
    setTemplates(prev => [...prev, { name: newTemplateName.trim(), email: recipient }]);
    setNewTemplateName('');
    setShowTemplates(false);
  };

  const deleteTemplate = (idx) => {
    setTemplates(prev => prev.filter((_, i) => i !== idx));
  };

  const applyTemplate = (tmpl) => {
    setRecipient(tmpl.email);
    setShowTemplates(false);
  };

  const getFileIcon = (file) => {
    if (!file) return <File size={24} />;
    const type = file.type || '';
    if (type.startsWith('image/')) return <Image size={24} className="text-purple-400" />;
    if (type.startsWith('video/')) return <Film size={24} className="text-pink-400" />;
    if (type.includes('pdf')) return <FileText size={24} className="text-red-400" />;
    return <File size={24} className="text-blue-400" />;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) setFiles(prev => [...prev, ...droppedFiles]);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const handleSend = async () => {
    if (files.length === 0 || !recipient) return;

    setSending(true);
    setProgress(0);
    setResult(null);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + Math.random() * 15;
      });
    }, 200);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('recipient', recipient);

      const res = await apiFetch(`${API_BASE}/api/send-file`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      clearInterval(progressInterval);
      setProgress(100);
      setResult({ success: data.success, message: data.message });
      if (data.success) {
        setTimeout(() => {
          setFiles([]);
          setProgress(0);
          setResult(null);
        }, 3000);
      }
    } catch {
      clearInterval(progressInterval);
      setProgress(0);
      setResult({ success: false, message: t('send_server_error') });
    }

    setTimeout(() => setSending(false), 500);
  };

  return (
    <div className="screen-enter pb-24 space-y-5">
      <h2 className="text-lg font-bold text-white/90">{t('send_title')}</h2>

      {files.length === 0 ? (
        <div
          id="drop-zone"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            drop-zone glass-card p-8 rounded-2xl cursor-pointer
            flex flex-col items-center justify-center gap-4 min-h-[200px]
            transition-all duration-300 active:scale-[0.98]
            ${dragOver ? 'drag-over border-blue-500/60 bg-blue-500/5' : ''}
          `}
        >
          <div className={`
            w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center
            transition-transform duration-300
            ${dragOver ? 'scale-110' : ''}
          `}>
            <Upload size={28} className="text-blue-400" />
          </div>
          <div className="text-center">
            <p className="text-sm text-white/70 font-medium">{t('send_drop_text')}</p>
            <p className="text-xs text-white/30 mt-1">{t('send_drop_or')}</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            multiple
          />
        </div>
      ) : (
        <div className="space-y-3">
          {files.map((file, index) => (
            <div key={index} className="glass-card p-4 animate-fade-in-up">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                  {getFileIcon(file)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white/90 truncate">{file.name}</p>
                  <p className="text-xs text-white/40 mt-0.5">{formatSize(file.size)}</p>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center
                             hover:bg-red-500/20 transition-colors active:scale-95"
                >
                  <X size={14} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}
          
          <div className="flex justify-end">
            <button
                onClick={() => fileInputRef.current?.click()}
                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
                {t('send_add_more')}
            </button>
            <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                multiple
            />
          </div>

          {sending && (
            <div className="mt-4 glass-card p-4">
              <div className="flex justify-between text-[10px] text-white/40 mb-1">
                <span>{t('send_loading')}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full progress-gradient rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-white/40 uppercase tracking-wider">
            {t('send_recipient_label')}
          </label>
          <button
            type="button"
            onClick={() => setShowTemplates(!showTemplates)}
            className="flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Bookmark size={12} />
            Shablonlar
          </button>
        </div>

        {showTemplates && (
          <div className="glass-card p-3 space-y-2 animate-fade-in-up">
            {templates.length > 0 ? (
              <div className="space-y-1.5 max-h-[120px] overflow-y-auto">
                {templates.map((tmpl, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                    <button type="button" onClick={() => applyTemplate(tmpl)} className="flex-1 text-left">
                      <p className="text-xs text-white/80 font-medium">{tmpl.name}</p>
                      <p className="text-[10px] text-white/40 font-mono">{tmpl.email}</p>
                    </button>
                    <button type="button" onClick={() => deleteTemplate(idx)} className="p-1 text-red-400 hover:text-red-300">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-white/30 text-center py-2">Shablonlar yo'q</p>
            )}
            <div className="flex gap-2 pt-2 border-t border-white/5">
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Shablon nomi..."
                className="flex-1 text-xs py-1.5"
              />
              <button
                type="button"
                onClick={saveTemplate}
                disabled={!newTemplateName.trim() || !recipient}
                className="px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 text-[10px] font-semibold disabled:opacity-30 hover:bg-blue-500/30 transition-colors"
              >
                <Plus size={12} className="inline" /> Saqlash
              </button>
            </div>
          </div>
        )}

        <input
          id="recipient-input"
          type="email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder={t('send_recipient_placeholder')}
          className="w-full"
        />
      </div>

      <button
        id="send-file-btn"
        onClick={handleSend}
        disabled={files.length === 0 || !recipient || sending}
        className="w-full btn-primary flex items-center justify-center gap-3
                   disabled:opacity-30 disabled:cursor-not-allowed"
      >
        {sending ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>{t('send_sending')}</span>
          </>
        ) : (
          <>
            <Send size={16} />
            <span>{t('send_button')}</span>
          </>
        )}
      </button>

      {result && (
        <div className={`
          glass-card p-4 flex items-center gap-3 animate-fade-in-up
          ${result.success ? 'border-emerald-500/20' : 'border-red-500/20'}
        `}>
          {result.success ? (
            <CheckCircle size={20} className="text-emerald-400 flex-shrink-0" />
          ) : (
            <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
          )}
          <p className={`text-sm font-medium ${result.success ? 'text-emerald-400' : 'text-red-400'}`}>
            {result.message}
          </p>
        </div>
      )}
    </div>
  );
};

export default SendFile;
